import { useCallback, useEffect, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../lib/api';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';

export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  isError?: boolean;
}

export interface Conversation {
  id: string;
  title: string;
  updated_at: string;
}

export interface Memory {
  id: string;
  content: string;
  category: string;
  created_at: string;
}

export const LAST_CONVERSATION_KEY = 'syqi.lastConversationId';

/** A deity-proposed action returned by /api/ai/deity (docs/DEITY_AGENT_BACKEND_DESIGN.md §1). */
export type DeityAction =
  | { kind: 'wrote'; table: string; op: 'insert' | 'update'; rowId?: string; summary: string }
  | { kind: 'proposeFormFill'; formKey: string; recordId?: string; fields: Record<string, unknown>; summary: string };

export interface DeityFormContext {
  formKey: string;
  recordId?: string;
  currentValues?: Record<string, unknown>;
}

export interface UseSyqiChatOptions {
  conversationId?: string;
  persistLastConversation?: boolean;
  /** Set to false to skip resuming the last (or fixed) conversation until this becomes true. */
  enabled?: boolean;
  /** Per-tool deity thread (docs/DEITY_AGENT_BACKEND_DESIGN.md §5). Omit for SyQi's
   *  global thread (100% unchanged default behavior). */
  toolId?: string;
  /** Defaults to '/api/ai/chat' (SyQi). Deity panels pass '/api/ai/deity'. */
  endpoint?: string;
  /** Called with the actions returned alongside the latest assistant reply. */
  onActions?: (actions: DeityAction[]) => void;
}

function lastConversationKey(toolId?: string): string {
  return toolId ? `deity.lastConversationId.${toolId}` : LAST_CONVERSATION_KEY;
}

export function useSyqiChat(options?: UseSyqiChatOptions) {
  const { session } = useAuth();
  const userId = session?.user.id;

  const toolId = options?.toolId;
  const endpoint = options?.endpoint ?? '/api/ai/chat';
  const storageKey = lastConversationKey(toolId);
  const onActions = options?.onActions;

  const fixedConversationId = options?.conversationId;
  const persistLastConversation = options?.persistLastConversation ?? !fixedConversationId;
  const enabled = options?.enabled ?? true;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const conversationIdRef = useRef<string | undefined>(fixedConversationId);
  const [conversationId, setConversationIdState] = useState<string | undefined>(fixedConversationId);

  const setConversationId = useCallback((id: string | undefined) => {
    conversationIdRef.current = id;
    setConversationIdState(id);
  }, []);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [memories, setMemories] = useState<Memory[]>([]);
  const [loadingMemories, setLoadingMemories] = useState(false);

  const loadConversationMessages = useCallback(async (convoId: string) => {
    setLoadingMessages(true);
    try {
      const { data } = await supabase
        .from('chat_messages')
        .select('id, role, content, created_at')
        .eq('conversation_id', convoId)
        .order('created_at', { ascending: true });
      setMessages(
        ((data || []) as any[]).map((m) => ({
          id: m.id,
          role: m.role,
          content: typeof m.content === 'string' ? m.content.replace(/\n\n\[Current step context: [\s\S]*\]$/, '') : m.content,
        }))
      );
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Resume the fixed conversation, or the last saved one, once enabled.
  useEffect(() => {
    if (!enabled || !userId) return;
    if (fixedConversationId) {
      if (conversationIdRef.current !== fixedConversationId) {
        setConversationId(fixedConversationId);
      }
      loadConversationMessages(fixedConversationId);
      return;
    }
    (async () => {
      const savedId = await AsyncStorage.getItem(storageKey);
      if (savedId && conversationIdRef.current !== savedId) {
        setConversationId(savedId);
        await loadConversationMessages(savedId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userId, fixedConversationId, storageKey]);

  const loadConversations = useCallback(async () => {
    if (!userId) return;
    setLoadingConversations(true);
    let query = supabase
      .from('chat_conversations')
      .select('id, title, updated_at')
      .eq('user_id', userId);
    query = toolId ? query.eq('tool_id', toolId) : query.is('tool_id', null);
    const { data } = await query.order('updated_at', { ascending: false });
    setConversations((data || []) as Conversation[]);
    setLoadingConversations(false);
  }, [userId, toolId]);

  const openConversation = useCallback(
    async (convoId: string) => {
      setConversationId(convoId);
      if (persistLastConversation) {
        await AsyncStorage.setItem(storageKey, convoId);
      }
      await loadConversationMessages(convoId);
    },
    [loadConversationMessages, persistLastConversation, setConversationId, storageKey]
  );

  const startNewChat = useCallback(async () => {
    setConversationId(undefined);
    if (persistLastConversation) {
      await AsyncStorage.removeItem(storageKey);
    }
    setMessages([]);
  }, [persistLastConversation, setConversationId, storageKey]);

  const deleteConversation = useCallback(
    async (convoId: string) => {
      await supabase.from('chat_conversations').delete().eq('id', convoId);
      setConversations((prev) => prev.filter((c) => c.id !== convoId));
      if (conversationIdRef.current === convoId) {
        setConversationId(undefined);
        if (persistLastConversation) {
          await AsyncStorage.removeItem(storageKey);
        }
        setMessages([]);
      }
    },
    [persistLastConversation, setConversationId, storageKey]
  );

  const loadMemories = useCallback(async () => {
    if (!userId) return;
    setLoadingMemories(true);
    const { data } = await supabase
      .from('syqi_memories')
      .select('id, content, category, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    setMemories((data || []) as Memory[]);
    setLoadingMemories(false);
  }, [userId]);

  const deleteMemory = useCallback(async (memoryId: string) => {
    await supabase.from('syqi_memories').delete().eq('id', memoryId);
    setMemories((prev) => prev.filter((m) => m.id !== memoryId));
  }, []);

  const send = useCallback(
    async (text: string, formContext?: DeityFormContext, stepContext?: Record<string, unknown>) => {
      const message = text.trim();
      if (!message || sending) return;
      setMessages((prev) => [...prev, { role: 'user', content: message }]);
      setSending(true);
      try {
        // v10: the NOW Step travels with the message so the deity answers about
        // what the user is actually doing. Sent as `stepContext` for routes that
        // read it, and folded into the message text (not the visible bubble) so
        // routes that don't yet still see it.
        const wire = stepContext
          ? `${message}\n\n[Current step context: ${JSON.stringify(stepContext)}]`
          : message;
        const body: Record<string, unknown> = { message: wire, conversationId: conversationIdRef.current };
        if (toolId) body.toolId = toolId;
        if (formContext) body.formContext = formContext;
        if (stepContext) body.stepContext = stepContext;
        const data = await apiFetch<{ conversationId: string; message: string; actions?: DeityAction[] }>(
          endpoint,
          {
            method: 'POST',
            body: JSON.stringify(body),
          }
        );
        const isNewConversation = !conversationIdRef.current;
        setConversationId(data.conversationId);
        if (persistLastConversation) {
          await AsyncStorage.setItem(storageKey, data.conversationId);
        }
        setMessages((prev) => [...prev, { role: 'assistant', content: data.message }]);
        if (isNewConversation) {
          loadConversations();
        }
        if (data.actions && data.actions.length > 0) {
          onActions?.(data.actions);
        }
        return data;
      } catch (e) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: e instanceof Error ? e.message : 'Something went wrong',
            isError: true,
          },
        ]);
        return undefined;
      } finally {
        setSending(false);
      }
    },
    [sending, persistLastConversation, setConversationId, loadConversations, toolId, endpoint, storageKey, onActions]
  );

  return {
    messages,
    sending,
    send,
    conversationId,
    setConversationId,
    conversations,
    loadingConversations,
    loadingMessages,
    loadConversations,
    reload: loadConversations,
    openConversation,
    startNewChat,
    deleteConversation,
    memories,
    loadingMemories,
    loadMemories,
    deleteMemory,
  };
}
